import {
  APP_PROVIDER_SEARCH,
  BACKEND,
  PROVIDER_SEARCH_VERSION_BAVARIA,
  PROVIDER_SEARCH_VERSION_MAP,
} from "core/consts";
import Config, {
  ENV_PRODUCTION,
  getApp,
  getBackendConfig,
} from "core/model/config";
import { getProviderSearchFromPathname } from "core/model/providerSearch";
import { DEFAULT_LOCAL_STORAGE_KEY, Session } from "core/model/session";
import { logNetEvent } from "core/model/utils/browser/NetDebugger";
import { isTest } from "core/model/utils/featureFlags";
import {
  AnyObject,
  AppType,
  Backend,
  ProviderSearch,
  ToType,
} from "core/types";
import { getUnixTime } from "date-fns";
import "whatwg-fetch";
import { getIgnoreTransportErrorReason } from "./shared";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    _test_token_expiration: string;
  }
}

type tokenAccessorType = () => string | undefined;
export let tokenAccessor: tokenAccessorType = () => undefined;

type authAccessorType = () => any | undefined;

let authAccessor: authAccessorType = () => undefined;

type tokenExpirationType = () => number | undefined;
let tokenExpiration: tokenExpirationType = () => undefined;

export function setTokenAccessor(newTokenAccessor: tokenAccessorType): void {
  tokenAccessor = newTokenAccessor;
}

export function setAuthAccessor(newAuthAccessor: authAccessorType): void {
  authAccessor = newAuthAccessor;
}

export function setTokenExpiration(
  newTokenExpiration: tokenExpirationType,
): void {
  tokenExpiration = newTokenExpiration;
}

type Options = {
  gqlFragmentHeader?: string;
  isApollo?: boolean;
  routeName?: string;
  token?: string;
  url?: string;
};

function shouldRetry(route: string, statusCode: number | undefined): boolean {
  if (route.startsWith("/auth")) return false;
  if (statusCode === 404) return false;

  return true;
}

function shouldLogoutUnauthorized(
  route: string,
  statusCode: number | undefined,
): boolean {
  if (route.startsWith("/auth")) return false;
  if ((window as any).IN_STORYBOOK) return false;
  return statusCode === 401;
}

function getStatusCode(error: ToType): number | undefined {
  try {
    const json = error?.message && JSON.parse(error.message);
    return json?.status;
  } catch (_e) {
    return undefined;
  }
}

const defaultMessage = "Failed request";

function redacAuthToken(authToken: string) {
  if (authToken.startsWith("Bearer ")) return "Bearer REDACTED";
  return authToken;
}

export function trackError(
  route: string,
  status: number,
  body: any,
  auth: string | undefined,
): void {
  const identification = authAccessor();
  const transportReason = getIgnoreTransportErrorReason(status, body, route);
  const message = body.message || defaultMessage;

  const authPart: AnyObject | { auth: string } = auth
    ? { auth: redacAuthToken(auth) }
    : {};
  const context = {
    ...authPart,
    body: JSON.stringify(body),
    email: identification?.account?.email || "",
    id: identification?.account?.id?.toString() || "",
    retry: body.isRetry ? "true" : "false",
    route,
    status: status.toString(),
    transportReason,
  };

  console.error(message, context);
}

async function fetch({
  fetchBody,
  headers,
  isApollo,
  isRetry = false,
  method,
  routeName,
  url,
}: {
  fetchBody: FormData | string | undefined;
  headers: AnyObject;
  isApollo: boolean | undefined;
  isRetry?: boolean;
  method: string;
  routeName: string | undefined;
  url: string;
}): Promise<any> {
  return window
    .fetch(url, {
      body: fetchBody,
      headers,
      method,
    })
    .then(async (response: Response) => {
      if (![200, 201, 204].includes(response.status)) {
        if (isApollo && response.status === 404) {
          throw new Error("Error 404 not found");
        }

        let body;
        try {
          body = await response.clone().json();
        } catch (_e) {
          // console.error(e)
        }

        const error = {
          body,
          response,
          status: response.status,
        };

        // since we let apollo handle error codes internally,
        // we log to datadog manually
        // if using fetch(transport) directly we want to throw the error
        // which will be caught and tracked in the "genericRequest" method
        if (isApollo) {
          trackError(
            routeName || url,
            response.status,
            {
              body,
              error,
              isRetry,
              message: `Bad HTTP status (${response.status})`,
              routeName,
              url,
            },
            headers.Authorization,
          );

          console.error(
            new Error(`${response.statusText} ${JSON.stringify(error)}`),
          );
        } else throw new Error(JSON.stringify(error));
      }

      return isApollo ? response : response.json();
    });
}

export function getBackendRouteUrl(backendConfig: any, route: string) {
  const routeUrl = backendConfig.url
    ? `${backendConfig.url}${route}`
    : `${backendConfig.protocol}://${backendConfig.host || ""}:${
        backendConfig.port || ""
      }${route}`;
  return routeUrl;
}

type ProviderSearchHeader = ProviderSearch | null;

const getProviderSearchHeader = (app: AppType): ProviderSearchHeader => {
  switch (app) {
    case APP_PROVIDER_SEARCH: {
      const providerSearch =
        getProviderSearchFromPathname() ?? PROVIDER_SEARCH_VERSION_BAVARIA;
      return PROVIDER_SEARCH_VERSION_MAP[providerSearch];
    }
    default:
      return null;
  }
};

function genericRequest({
  backend = BACKEND.MATCHING,
  body,
  method,
  options = {},
  queryParams,
  route = "",
}: {
  body: AnyObject | Array<any> | null | undefined;
  method: "DELETE" | "GET" | "POST" | "PUT";
  options: Options | undefined;
  queryParams: AnyObject | null | undefined;
  route: string;
  backend?: Backend;
}): Promise<any> {
  const { isApollo, routeName, token, url: optionalUrl } = options;

  let bearer = token || tokenAccessor() || "";

  // hack for signup where JWT is passed in the body's variables
  const jwtInApolloVariables =
    body && "variables" in body && body.variables.jwt;

  if (jwtInApolloVariables) {
    bearer = jwtInApolloVariables;
    delete body.variables.jwt;
  }

  if (bearer && queryParams) {
    delete queryParams.token;
  }

  const testExpiration =
    Config.environment !== ENV_PRODUCTION
      ? window._test_token_expiration
      : undefined;
  const expiration = testExpiration ?? tokenExpiration();

  if (
    expiration &&
    Number(expiration) < getUnixTime(new Date()) &&
    !route.startsWith("/auth")
  ) {
    window.location.href = "/logout?reason=session_expired";
    return Promise.resolve(null);
  }

  // default transport requires constructing the url here
  // apollo constructs the route part in the component
  let url = route;

  if (optionalUrl) {
    url = `${optionalUrl}${route}`;
  } else {
    const backendConfig = getBackendConfig(backend);
    if (backendConfig) {
      url = getBackendRouteUrl(backendConfig, route);
    }
  }
  if (queryParams && Object.keys(queryParams).length > 0) {
    // They should be URI Encoded but the status are in this format status=1,2,3
    const query = Object.keys(queryParams)
      .map((key) => {
        if (queryParams?.[key] && Array.isArray(queryParams[key])) {
          return queryParams[key].map((s: string) => `status=${s}`).join("&");
        }

        if (!queryParams) return null;
        return `${key}=${queryParams[key]}`;
      })
      .join("&");
    url = `${url}?${query}`;
  }

  const headers: {
    Accept: string;
    Authorization?: string;
    "Content-Type"?: string;
    "X-Custom-recare-app-id"?: number;
    "X-Custom-recare-app-version"?: string;
    "X-Custom-recare-origin"?: string;
    "X-Custom-recare-provider-search-state"?: ProviderSearchHeader;
    "X-Custom-recare-session-id"?: string;
    "X-Custom-recare-session-item"?: number;
  } = {
    Accept: "*/*",
  };

  let fetchBody: FormData | string | undefined;
  if (method === "POST" && body instanceof FormData) {
    fetchBody = body;
  } else {
    fetchBody = body ? JSON.stringify(body) : undefined;
    headers["Content-Type"] = "application/json";
  }

  if (window?.location?.origin) {
    headers["X-Custom-recare-origin"] = window.location.origin;
  }

  const app = getApp();
  if (app) {
    headers["X-Custom-recare-app-id"] = app;

    const providerSearchHeader = getProviderSearchHeader(app);
    if (providerSearchHeader != null) {
      headers["X-Custom-recare-provider-search-state"] = providerSearchHeader;
    }
  } else {
    if (!isTest)
      console.error(`no app in generic request`, {
        location: window?.location,
        url,
      });
  }
  if (Config.version) headers["X-Custom-recare-app-version"] = Config.version;

  const session = Session.getSessionFromLocalStorage(DEFAULT_LOCAL_STORAGE_KEY);

  if (session?.id) headers["X-Custom-recare-session-id"] = session.id;
  if (session?.trackingEventsCount)
    headers["X-Custom-recare-session-item"] = session.trackingEventsCount;

  if (bearer != "") headers.Authorization = `Bearer ${bearer}`;

  return fetch({ fetchBody, headers, isApollo, method, routeName, url })
    .catch((errorFirst) => {
      const statusCode = getStatusCode(errorFirst);

      if (shouldLogoutUnauthorized(route, statusCode)) {
        window.location.href = "/logout?reason=unauthorized";
        return Promise.resolve(null);
      }

      if (!shouldRetry(route, statusCode)) throw errorFirst;

      return fetch({
        fetchBody,
        headers,
        isApollo,
        isRetry: true,
        method,
        routeName,
        url,
      }).catch((errorSecond) => {
        let responseStatus;
        try {
          responseStatus = JSON.parse(errorSecond.message).status;
        } catch (_e) {
          responseStatus = -1;
        }
        trackError(
          routeName || url,
          responseStatus,
          {
            error: errorSecond,
            isRetry: true,
            message: defaultMessage,
            routeName,
            url,
          },
          headers.Authorization,
        );
        throw errorSecond;
      });
    })
    .then((res) => {
      logNetEvent({ method, reqBody: fetchBody, resBody: res || "200", url });
      return res;
    })
    .catch((err) => {
      logNetEvent({ err, method, reqBody: fetchBody, url });
      throw err;
    });
}

type getType = {
  options?: Options;
  queryParams?: AnyObject | null | undefined;
  route: string;
  backend?: Backend;
};
function get<Response>({
  backend,
  options,
  queryParams,
  route,
}: getType): Promise<Response> {
  return genericRequest({
    backend,
    body: null,
    method: "GET",
    options,
    queryParams,
    route,
  });
}

type postType = {
  body: AnyObject | Array<any> | null | undefined;
  options?: Options;
  queryParams?: AnyObject | null | undefined;
  route: string;
  backend?: Backend;
};
function post<Response>({
  backend,
  body,
  options,
  queryParams,
  route,
}: postType): Promise<Response> {
  return genericRequest({
    backend,
    body,
    method: "POST",
    options,
    queryParams,
    route,
  });
}

type delType = {
  body: AnyObject;
  options?: Options;
  route: string;
  backend?: Backend;
};
function del<Response>({
  backend,
  body,
  options,
  route,
}: delType): Promise<Response> {
  return genericRequest({
    backend,
    body,
    method: "DELETE",
    options,
    queryParams: null,
    route,
  });
}

type putType = {
  body: AnyObject | Array<any>;
  options?: Options;
  queryParams?: AnyObject | null | undefined;
  route: string;
  backend?: Backend;
};
function put<Response>({
  backend,
  body,
  options,
  queryParams,
  route,
}: putType): Promise<Response> {
  return genericRequest({
    backend,
    body,
    method: "PUT",
    options,
    queryParams,
    route,
  });
}

type uploadFileType = {
  file: File;
  options?: Options;
};
function uploadFile<Response>({
  file,
  options = {},
}: uploadFileType): Promise<Response> {
  const formData = new FormData();
  formData.append("file", file);
  return genericRequest({
    body: formData,
    method: "POST",
    options,
    queryParams: null,
    route: "/upload",
  });
}

export default {
  del,
  get,
  post,
  put,
  setAuthAccessor,
  setTokenAccessor,
  setTokenExpiration,
  uploadFile,
};
